/**
 * Work rules — "what is waiting on whom", declared beside the validations.
 *
 * Most work in a business application is not an approval. It is records
 * sitting in a state, waiting for a role: an approved order nobody has
 * received against, a qualified lead with no deal raised, an invoice past its
 * due date. Nothing else in the model expresses that, so this editor does.
 *
 * A rule is a PREDICATE, not a trigger: while its condition holds, the task
 * exists; when it stops holding, the task closes. There is no separate "clear
 * it" step to forget, which is why the editor asks for a condition rather than
 * an event.
 *
 * The condition uses the SAME expression language as a validation and an
 * approval level's applies-when, so anyone who can write one can write this.
 */

import { useCallback, useMemo, useState } from 'react'
import { ClipboardList, Plus, Trash2, FlaskConical } from 'lucide-react'
import toast from 'react-hot-toast'

import type { TransactionModel } from '@twasta/page-runtime/txn/types'
import { livePreviewExecute } from '@/livepreview/api'

interface WorkRuleAssign {
  roles?: string[]
  user_columns?: string[]
  users?: string[]
}

interface WorkRuleTarget {
  open?: string
  obj_name?: string
  form_no?: number
  verb?: string
  seed?: Record<string, string>
}

export interface WorkRule {
  name: string
  title: string
  summary?: string
  kind?: string
  trigger?: { kind?: string; anchor?: string; offset_days?: number }
  condition?: string
  assign?: WorkRuleAssign
  due?: { column?: string; warn_days?: number }
  priority?: string
  target?: WorkRuleTarget
  suppress_when?: string[]
  disabled?: boolean
  managed_by?: string
}

const OPEN_KINDS = [
  { value: 'record', label: 'Open this record' },
  { value: 'create', label: 'Create a record on another object' },
  { value: 'contact', label: 'Contact someone (writes nothing)' },
  { value: 'checklist', label: 'Checklist / confirm' },
  { value: 'url', label: 'Open a link' },
]

export default function WorkRulesEditor({
  model, onModelChange, project,
}: {
  model: TransactionModel & { work_rules?: WorkRule[] }
  onModelChange: (next: TransactionModel) => void
  project: string
}) {
  const rules = useMemo<WorkRule[]>(
    () => (Array.isArray(model.work_rules) ? model.work_rules : []), [model.work_rules])
  const [openIdx, setOpenIdx] = useState<number>(rules.length ? 0 : -1)
  const [testing, setTesting] = useState('')

  const columns = useMemo(() => {
    const sm = model.sql_models?.[0]?.sql_model
    return (sm?.columns || []).map((c) => {
      // The on-disk model wraps each column; the shared type does not.
      const wrapped = c as unknown as { column?: { name?: string } }
      const col = wrapped.column || (c as unknown as { name?: string })
      return String(col?.name || '')
    }).filter(Boolean)
  }, [model])

  const patch = useCallback((next: WorkRule[]) => {
    onModelChange({ ...model, work_rules: next } as TransactionModel)
  }, [model, onModelChange])

  const update = useCallback((idx: number, changes: Partial<WorkRule>) => {
    patch(rules.map((r, i) => (i === idx ? { ...r, ...changes } : r)))
  }, [rules, patch])

  const add = useCallback(() => {
    const n = rules.length + 1
    patch([...rules, {
      name: `work_rule_${n}`,
      title: 'Do the thing for {form.' + (columns[0] || 'id') + '}',
      trigger: { kind: 'state' },
      condition: '',
      assign: { roles: [] },
      target: { open: 'record' },
      suppress_when: ['approval'],
    }])
    setOpenIdx(rules.length)
  }, [rules, patch, columns])

  /**
   * Run the rule against live rows before shipping it.
   *
   * A condition that is broader than its author intended does not fail — it
   * quietly raises a task per matching record, which on a real table can mean
   * thousands. Seeing the number first is the whole point of this button.
   */
  const test = useCallback(async (rule: WorkRule) => {
    setTesting(rule.name)
    try {
      const res = await livePreviewExecute(project, model.obj_name, 'worklist_test_rule', {
        obj_name: model.obj_name, rule,
      }) as { matched?: number; sample?: string[]; assignees?: string[]; error?: string }
      if (res?.error) { toast.error(res.error); return }
      const n = res?.matched ?? 0
      const who = (res?.assignees || []).join(', ')
      toast.success(
        n === 0
          ? 'No records match right now — nobody would be asked to do anything.'
          : `${n} record${n === 1 ? '' : 's'} match${n === 1 ? 'es' : ''}` +
            (who ? `, waiting on ${who}.` : '.'),
        { duration: 6000 },
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not test that rule.')
    } finally {
      setTesting('')
    }
  }, [project, model.obj_name])

  return (
    <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ClipboardList size={16} />
        <strong>Work rules</strong>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={add} style={btnPrimary}>
          <Plus size={14} /> Add rule
        </button>
      </div>

      <p style={hint}>
        A rule puts this object&apos;s records into somebody&apos;s worklist while its
        condition holds, and takes them out again when it stops. Approvals are
        already handled — use a rule for the work that follows.
      </p>

      {rules.length === 0 && (
        <div style={{ ...hint, padding: '1.5rem', textAlign: 'center' }}>
          No work rules yet. Nothing from this object will appear in anyone&apos;s
          worklist.
        </div>
      )}

      {rules.map((rule, idx) => {
        const isOpen = openIdx === idx
        const trigger = rule.trigger?.kind || 'state'
        const managed = !!rule.managed_by
        return (
          <div key={rule.name + idx} style={card}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
              onClick={() => setOpenIdx(isOpen ? -1 : idx)}
            >
              <strong style={{ fontSize: '0.9425rem' }}>{rule.name}</strong>
              {managed && (
                <span style={badge} title={`Compiled from ${rule.managed_by}`}>
                  from {rule.managed_by}
                </span>
              )}
              {rule.disabled && <span style={{ ...badge, opacity: 0.7 }}>off</span>}
              <span style={{ flex: 1 }} />
              <button
                type="button" style={btnGhost} disabled={testing === rule.name}
                onClick={(e) => { e.stopPropagation(); void test(rule) }}
                title="Count the records this rule would raise a task for, right now"
              >
                <FlaskConical size={13} /> Test
              </button>
              <button
                type="button" style={btnGhost}
                onClick={(e) => {
                  e.stopPropagation()
                  patch(rules.filter((_, i) => i !== idx))
                  setOpenIdx(-1)
                }}
              >
                <Trash2 size={13} />
              </button>
            </div>

            {isOpen && (
              <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
                {managed && (
                  <div style={{ ...hint, color: '#b45309' }}>
                    This rule is compiled from a process flow. Edit it there —
                    changes made here are replaced on the next save of that flow.
                  </div>
                )}
                <Field label="Name" hint="Part of the task identity — changing it closes the old tasks and opens new ones.">
                  <input style={input} value={rule.name}
                    onChange={(e) => update(idx, { name: e.target.value })} />
                </Field>
                <Field label="Title" hint="What the person sees. Use {form.Column} to fill in the record's own values.">
                  <input style={input} value={rule.title}
                    onChange={(e) => update(idx, { title: e.target.value })} />
                </Field>
                <Field label="Summary" hint="Optional second line.">
                  <input style={input} value={rule.summary || ''}
                    onChange={(e) => update(idx, { summary: e.target.value })} />
                </Field>

                <Field label="When" hint="A state rule waits for the condition; a reminder fires relative to a date.">
                  <select style={input} value={trigger}
                    onChange={(e) => update(idx, {
                      trigger: { ...(rule.trigger || {}), kind: e.target.value },
                      kind: e.target.value === 'temporal' ? 'reminder' : 'work',
                    })}>
                    <option value="state">While a condition is true</option>
                    <option value="temporal">Before or after a date</option>
                    <option value="event">When business logic raises it</option>
                  </select>
                </Field>

                {trigger === 'temporal' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <Field label="Date column">
                      <select style={input} value={rule.trigger?.anchor || ''}
                        onChange={(e) => update(idx, {
                          trigger: { ...(rule.trigger || {}), anchor: e.target.value },
                        })}>
                        <option value="">Choose a column…</option>
                        {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </Field>
                    <Field label="Days offset" hint="Negative is before the date, e.g. -2 for two days before.">
                      <input style={input} type="number" value={rule.trigger?.offset_days ?? 0}
                        onChange={(e) => update(idx, {
                          trigger: { ...(rule.trigger || {}), offset_days: Number(e.target.value) },
                        })} />
                    </Field>
                  </div>
                )}

                <Field
                  label="Condition"
                  hint='Same language as a validation, e.g. {form.status} == "APPROVED" and {form.received} == "N". Leave blank to always apply.'
                >
                  <input style={input} value={rule.condition || ''}
                    onChange={(e) => update(idx, { condition: e.target.value })} />
                </Field>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <Field label="Assign to roles" hint="Comma separated. Everyone in the role sees it; one person acting closes it.">
                    <input style={input} value={(rule.assign?.roles || []).join(', ')}
                      onChange={(e) => update(idx, {
                        assign: {
                          ...(rule.assign || {}),
                          roles: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                        },
                      })} />
                  </Field>
                  <Field label="…and the person named in" hint="A column holding a user, e.g. the record's owner.">
                    <select style={input} value={(rule.assign?.user_columns || [])[0] || ''}
                      onChange={(e) => update(idx, {
                        assign: {
                          ...(rule.assign || {}),
                          user_columns: e.target.value ? [e.target.value] : [],
                        },
                      })}>
                      <option value="">Nobody in particular</option>
                      {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <Field label="Due date from" hint="Drives the overdue flag in the worklist.">
                    <select style={input} value={rule.due?.column || ''}
                      onChange={(e) => update(idx, {
                        due: { ...(rule.due || {}), column: e.target.value },
                      })}>
                      <option value="">No due date</option>
                      {columns.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Button says" hint="The verb on the worklist row — what the person is being asked to do.">
                    <input style={input} value={rule.target?.verb || ''}
                      placeholder="e.g. Receive goods"
                      onChange={(e) => update(idx, {
                        target: { ...(rule.target || {}), verb: e.target.value },
                      })} />
                  </Field>
                </div>

                <Field label="Clicking it opens">
                  <select style={input} value={rule.target?.open || 'record'}
                    onChange={(e) => update(idx, {
                      target: { ...(rule.target || {}), open: e.target.value },
                    })}>
                    {OPEN_KINDS.map((k) => (
                      <option key={k.value} value={k.value}>{k.label}</option>
                    ))}
                  </select>
                </Field>

                {rule.target?.open === 'create' && (
                  <>
                    <Field label="Object to create" hint="The transaction the person fills in next.">
                      <input style={input} value={rule.target?.obj_name || ''}
                        onChange={(e) => update(idx, {
                          target: { ...(rule.target || {}), obj_name: e.target.value },
                        })} />
                    </Field>
                    <Field
                      label="Pre-fill"
                      hint="One per line, as target_column = {form.SourceColumn}. These arrive already filled in, so the person keys as little as possible."
                    >
                      <textarea
                        style={{ ...input, minHeight: 90, fontFamily: 'monospace', fontSize: '0.8425rem' }}
                        value={Object.entries(rule.target?.seed || {})
                          .map(([k, v]) => `${k} = ${v}`).join('\n')}
                        onChange={(e) => {
                          const seed: Record<string, string> = {}
                          for (const line of e.target.value.split('\n')) {
                            const eq = line.indexOf('=')
                            if (eq < 0) continue
                            const k = line.slice(0, eq).trim()
                            const v = line.slice(eq + 1).trim()
                            if (k) seed[k] = v
                          }
                          update(idx, { target: { ...(rule.target || {}), seed } })
                        }}
                      />
                    </Field>
                  </>
                )}

                <Field
                  label="Stay quiet while"
                  hint="Providers whose open task on the same record silences this rule. Default: an approval — don't nag somebody about a record that is mid sign-off."
                >
                  <input style={input}
                    value={(rule.suppress_when ?? ['approval']).join(', ')}
                    onChange={(e) => update(idx, {
                      suppress_when: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                    })} />
                </Field>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8825rem' }}>
                  <input type="checkbox" checked={!!rule.disabled}
                    onChange={(e) => update(idx, { disabled: e.target.checked })} />
                  Turn this rule off (existing tasks close)
                </label>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: '0.8425rem', fontWeight: 600 }}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary, #64748b)' }}>{hint}</span>}
    </div>
  )
}

const card: React.CSSProperties = {
  border: '1px solid var(--color-border, #e2e8f0)', borderRadius: 10,
  padding: '0.75rem', background: 'var(--color-bg-secondary, #f8fafc)',
}
const input: React.CSSProperties = {
  width: '100%', padding: '0.4rem 0.55rem', borderRadius: 6, fontSize: '0.8825rem',
  border: '1px solid var(--color-border, #cbd5e1)',
  background: 'var(--color-bg, #fff)', color: 'var(--color-text, #0f172a)',
}
const hint: React.CSSProperties = {
  fontSize: '0.8425rem', color: 'var(--color-text-secondary, #64748b)', margin: 0,
}
const btnPrimary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
  padding: '0.35rem 0.7rem', borderRadius: 8, fontSize: '0.8625rem', fontWeight: 600,
  border: '1px solid var(--color-accent, #4c8dff)',
  background: 'var(--color-accent, #4c8dff)', color: '#fff',
}
const btnGhost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
  padding: '0.25rem 0.55rem', borderRadius: 6, fontSize: '0.8225rem',
  border: '1px solid var(--color-border, #e2e8f0)', background: 'transparent',
  color: 'var(--color-text-secondary, #475569)',
}
const badge: React.CSSProperties = {
  fontSize: '0.8125rem', fontWeight: 700, padding: '2px 7px', borderRadius: 999,
  background: 'var(--color-bg, #fff)', border: '1px solid var(--color-border, #e2e8f0)',
  color: 'var(--color-text-secondary, #64748b)',
}
